import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  LinearProgress,
  Paper,
  Slider,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import DetailsBox, {
  DetailRow,
} from '../../../../../components/Cards/DetailsBox'
import SmallModal from '../../../../../components/Modals/SmallModal'
import SimpleTextField from '../../../../../components/Forms/SimpleTextField'
import { GlobalContext, TenantSettingsContext } from '../../../../../App'
import { formatMoney } from '../../../../../helpers/currency'
import AddShiftPopup from '../AddShiftPopup'
import API from '../../../../../services/API'
import moment from 'moment'
import EventClientPanel from '../EventClientPanel/EventClientPanel'
import { useNavigate } from 'react-router-dom'
import Personnel from './Personnel'
import { LoadingSmall } from '../../../../loading/loadingPage'

const QuoteBuilder = ({
  existingEvent,
  hasExistingQuote,
  viewOnly,
  onClose,
}) => {
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const [loading, setLoading] = useState(true)
  const [eventRef, setEventRef] = useState(existingEvent ?? {})
  const [personnel, setPersonnel] = useState([])
  const [accomodation, setAccomodation] = useState(0)
  const [chargeAccomToCustomer, setChargeAccomToCustomer] = useState(true)
  const [chargeMileageToCustomer, setChargeMileageToCustomer] = useState(true)
  const [vehicles, setVehicles] = useState(0)
  const [mileage, setMileage] = useState(0)
  const [treatmentCentreBuild, setTreatmentCentreBuild] = useState(0)
  const [adminCharge, setAdminCharge] = useState(120)
  const [profitMargin, setProfitMargin] = useState(40)
  const [addShift, setAddShift] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState(existingEvent ?? null)
  const [selectedOrganisation, setSelectedOrganisation] = useState(
    eventRef?.organisation ?? null,
  )
  const [discount, setDiscount] = useState(0)
  const [quoteMeta, setQuoteMeta] = useState(null)
  const [storedBreakdown, setStoredBreakdown] = useState(null)
  const [declineReasonInput, setDeclineReasonInput] = useState('')
  const [openDecline, setOpenDecline] = useState(false)
  const [actioning, setActioning] = useState(false)
  const hasExistingEvent =
    existingEvent && Object.keys(existingEvent).length > 0
  const [summaryOfChanges, setSummaryOfChanges] = useState('')
  const { setToast } = useContext(GlobalContext)
  const navigate = useNavigate()
  const [defaultSettings, setDefaultSettings] = useState({
    makeReadyCost: 0,
    costPerMile: 0,
    defaultTreatmentCentreBuildCost: 0,
    defaultAdminCharge: 0,
    schedulingCharge: 0,
  })

  useEffect(() => {
    API.get(`/settings?select=quoteInvoices`).then((settingsResponse) => {
      if (settingsResponse.status === 200) {
        setDefaultSettings(settingsResponse.data.quoteInvoices)
        if (hasExistingQuote) {
          API.get('/clients/quote?quoteID=' + hasExistingQuote)
            .then((res) => {
              if (res.status === 200) {
                setPersonnel(res.data.personnel)
                setAccomodation(res.data.accommodationCharge)
                setChargeAccomToCustomer(res.data.chargeAccommodationToCustomer)
                setChargeMileageToCustomer(!!res.data.chargeMileageToCustomer)
                setVehicles(res.data.numberVehicles)
                setMileage(res.data.numberMiles)
                setTreatmentCentreBuild(res.data.treatmentCentreBuilds)
                setAdminCharge(res.data.adminCharge)
                setProfitMargin(res.data.profitMargin)
                setDiscount(res.data.discount)
                setSummaryOfChanges(res.data.summaryOfChanges)
                setQuoteMeta({
                  _id: res.data._id,
                  status: res.data.status,
                  sentAt: res.data.sentAt,
                  validUntil: res.data.validUntil,
                  acceptedAt: res.data.acceptedAt,
                  declinedAt: res.data.declinedAt,
                  declineReason: res.data.declineReason,
                })
                // Snapshot of the totals as actually stored on the quote —
                // used for display in view-only mode so it can't drift from
                // what's shown elsewhere (the documents grid, invoice
                // generator) just because tenant-wide default pricing
                // settings (cost per mile, make-ready cost, etc.) changed
                // since this quote was created. Live recompute below is only
                // appropriate while actively editing/repricing.
                setStoredBreakdown({
                  personnel: res.data.summary?.personnel ?? 0,
                  vehiclesMileage: res.data.summary?.vehiclesMileage ?? 0,
                  scheduling: res.data.businessCosts?.scheduling ?? 0,
                  makeReady: res.data.businessCosts?.makeReady ?? 0,
                  accomodation: res.data.businessCosts?.accomodation ?? res.data.accommodationCharge ?? 0,
                  totalInvoice: res.data.totalInvoice ?? 0,
                  totalCost: res.data.totalCost ?? 0,
                })
              }
            })
            .finally(() => setLoading(false))
        } else {
          setAdminCharge(settingsResponse.data.quoteInvoices.defaultAdminCharge)
          setTreatmentCentreBuild(
            settingsResponse.data.quoteInvoices.defaultTreatmentCentreBuildCost,
          )
          setProfitMargin(
            settingsResponse.data.quoteInvoices.defaultProfitMargin,
          )
          setLoading(false)
        }
      } else {
        // Without this the builder spins forever on a failed settings load
        setLoading(false)
        setToast({
          message:
            settingsResponse?.data?.error || 'Unable to load quote settings.',
          severity: 'error',
        })
      }
    })
  }, [hasExistingQuote])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!selectedOrganisation?._id || !selectedEvent?._id) {
      setToast({
        message: 'Choose an event and organisation before saving the quote.',
        severity: 'error',
      })
      return
    }
    const body = {
      personnel,
      client: selectedOrganisation._id,
      event: selectedEvent._id,
      numberVehicles: vehicles,
      numberMiles: mileage,
      accommodationCharge: accomodation,
      chargeAccommodationToCustomer: chargeAccomToCustomer,
      chargeMileageToCustomer: chargeMileageToCustomer,
      treatmentCentreBuilds: treatmentCentreBuild,
      adminCharge,
      summaryOfChanges,
      summary: {
        personnel: totalWages,
        vehiclesMileage: totalVehicles,
        administration: adminCharge,
      },
      businessCosts: {
        scheduling: totalScheduling,
        makeReady: totalMakeReady,
        accomodation: accomodation,
      },
      profitMargin: profitMargin,
      totalInvoice: totalInvoice,
      totalCost: totalCost,
      discount,
      amountDue: totalInvoice - discount,
    }

    // Editing a draft updates it in place. Anything past Draft is a record
    // of what was offered, so editing it saves a NEW quote (the server
    // refuses in-place edits) - the pencil used to silently create a new
    // quote in every case while its action buttons still pointed at the old
    // one.
    const editingDraft = quoteMeta && quoteMeta.status === 'Draft'
    const request = editingDraft
      ? API.put(`/clients/quote/${quoteMeta._id}`, body)
      : API.post('/clients/quote', body)

    request
      .then((res) => {
        if (res.status === 200) {
          navigate('/events/' + selectedEvent._id)
          setToast({
            message: editingDraft
              ? 'Quote Updated'
              : quoteMeta
                ? 'New quote version created'
                : 'Quote Added',
          })
          if (onClose) {
            onClose()
          }
        } else {
          setToast({
            message: res?.data?.error || 'Unable to save quote.',
            severity: 'error',
          })
        }
      })
      .catch((e) => {
        setToast({
          message: 'Unable to save quote. Please try again later.',
          severity: 'error',
        })
        console.log(e)
      })
  }

  // API.js sets validateStatus: () => true, so axios never rejects on
  // 4xx/5xx — only real network failures land in catch. Every call here
  // must check res.status explicitly or a rejected request fails silently.
  const handleSend = () => {
    setActioning(true)
    API.post(`/clients/quote/${quoteMeta._id}/send`)
      .then((res) => {
        if (res.status === 200) {
          setQuoteMeta((prev) => ({ ...prev, ...res.data }))
          setToast({ message: 'Quote sent' })
        } else {
          setToast({
            message: res?.data?.error || 'Unable to send quote.',
            severity: 'error',
          })
        }
      })
      .catch(() => {
        setToast({
          message: 'Unable to send quote. Please try again later.',
          severity: 'error',
        })
      })
      .finally(() => setActioning(false))
  }

  const handleAccept = () => {
    setActioning(true)
    API.post(`/clients/quote/${quoteMeta._id}/accept`, { channel: 'Internal' })
      .then((res) => {
        if (res.status === 200) {
          setQuoteMeta((prev) => ({ ...prev, ...res.data }))
          setToast({ message: 'Quote marked as accepted' })
        } else {
          setToast({
            message: res?.data?.error || 'Unable to accept quote.',
            severity: 'error',
          })
        }
      })
      .catch(() => {
        setToast({
          message: 'Unable to accept quote. Please try again later.',
          severity: 'error',
        })
      })
      .finally(() => setActioning(false))
  }

  const handleDecline = () => {
    setActioning(true)
    API.post(`/clients/quote/${quoteMeta._id}/decline`, {
      reason: declineReasonInput,
    })
      .then((res) => {
        if (res.status === 200) {
          setQuoteMeta((prev) => ({ ...prev, ...res.data }))
          setToast({ message: 'Quote marked as declined' })
          setOpenDecline(false)
        } else {
          setToast({
            message: res?.data?.error || 'Unable to decline quote.',
            severity: 'error',
          })
        }
      })
      .catch(() => {
        setToast({
          message: 'Unable to decline quote. Please try again later.',
          severity: 'error',
        })
      })
      .finally(() => setActioning(false))
  }

  const rawWages = personnel.reduce((a, b) => a + b.rate, 0)
  const mileageTotal = mileage * vehicles * defaultSettings.costPerMile
  const rawVehicles = vehicles * defaultSettings.makeReadyCost
  const totalScheduling =
    Object.keys(personnel).length * defaultSettings.schedulingCharge
  const totalMakeReady = vehicles * defaultSettings.makeReadyCost
  const totalWages = rawWages + (rawWages * profitMargin) / 100
  let totalVehicles = rawVehicles + (rawVehicles * profitMargin) / 100

  if (chargeMileageToCustomer) {
    totalVehicles += mileageTotal
  }

  // mileageTotal is already folded into totalVehicles above when charged to
  // the customer — it must not be added again here (this used to
  // double-count it; the server-side calculateQuotePricing helper mirrors
  // this same, corrected formula).
  const totalInvoice = parseFloat(
    totalWages +
      totalVehicles +
      (chargeAccomToCustomer && accomodation) +
      treatmentCentreBuild +
      adminCharge,
  )

  const totalCost =
    totalScheduling + totalMakeReady + rawWages + accomodation + mileageTotal

  // In view-only mode, show the figures actually stored on the quote rather
  // than the live recompute above — otherwise a change to tenant-wide
  // default pricing settings after this quote was created would make the
  // "opened" total disagree with the total shown in the documents grid /
  // invoice generator (both of which read the stored value).
  const usingStoredBreakdown = viewOnly && !!storedBreakdown
  const displayPersonnel = usingStoredBreakdown ? storedBreakdown.personnel : totalWages
  // totalVehicles already folds in mileageTotal when chargeMileageToCustomer
  // is on (see above) — adding mileageTotal again here would double-count it
  // in this summary line, same class of bug as the totalInvoice fix above.
  const displayVehiclesMileage = usingStoredBreakdown
    ? storedBreakdown.vehiclesMileage
    : totalVehicles
  const displayScheduling = usingStoredBreakdown ? storedBreakdown.scheduling : totalScheduling
  const displayMakeReady = usingStoredBreakdown ? storedBreakdown.makeReady : totalMakeReady
  const displayAccomodation = usingStoredBreakdown ? storedBreakdown.accomodation : accomodation
  const displayTotalInvoice = usingStoredBreakdown ? storedBreakdown.totalInvoice : totalInvoice
  const displayTotalCost = usingStoredBreakdown ? storedBreakdown.totalCost : totalCost

  const quoteStatusColors = {
    Draft: 'default',
    Sent: 'info',
    Accepted: 'success',
    Declined: 'error',
    Expired: 'warning',
  }

  return (
    <Box display="flex" gap={1} flexDirection="column">
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography variant="title2">
          {viewOnly ? 'View' : hasExistingQuote ? 'Edit' : 'Create New'} Quote{' '}
          {eventRef?.title && (
            <>
              for <b>{existingEvent?.title}</b>
            </>
          )}
        </Typography>
        {quoteMeta?.status && (
          <Chip
            size="small"
            label={quoteMeta.status}
            color={quoteStatusColors[quoteMeta.status] || 'default'}
          />
        )}
      </Stack>
      {loading ? (
        <LoadingSmall />
      ) : (
        <Grid container spacing={2} component="form" onSubmit={handleSubmit}>
          <Grid
            item
            xs={12}
            lg={8}
            display="flex"
            flexDirection="column"
            gap={2}
          >
            <EventClientPanel
              eventRef={eventRef}
              setEventRef={setEventRef}
              hasExistingEvent={hasExistingEvent}
              selectedEvent={selectedEvent}
              setSelectedEvent={setSelectedEvent}
              selectedOrganisation={selectedOrganisation}
              setSelectedOrganisation={setSelectedOrganisation}
            />
            <Paper sx={{ padding: 1 }}>
              <DetailsBox
                title="Personnel"
                buttonRight={
                  <Button
                    size="small"
                    onClick={() => setAddShift(true)}
                    variant="contained"
                  >
                    Add Shift
                  </Button>
                }
              >
                <Personnel
                  setAddShift={setAddShift}
                  personnel={personnel}
                  setPersonnel={setPersonnel}
                  addShift={addShift}
                  profitMargin={profitMargin}
                />
              </DetailsBox>
            </Paper>
            <Paper sx={{ padding: 1 }}>
              <DetailsBox title="Additional Charges">
                <Grid container spacing={2}>
                  <Grid item xs={12} lg={3.5}>
                    <TextField
                      type="number"
                      value={vehicles}
                      onChange={(e) =>
                        e.target.value >= 0 &&
                        setVehicles(parseInt(e.target.value))
                      }
                      InputProps={{
                        inputProps: { min: 0 },
                      }}
                      size="small"
                      label="Number of Vehicles"
                      autoComplete="off"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} lg={3.5}>
                    <TextField
                      type="number"
                      value={mileage}
                      InputProps={{
                        inputProps: { min: 0 },
                      }}
                      onChange={(e) =>
                        e.target.value >= 0 &&
                        setMileage(parseInt(e.target.value))
                      }
                      size="small"
                      label="Number of Miles"
                      autoComplete="off"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} lg={5}>
                    <FormControlLabel
                      label="Charge Mileage to Customer"
                      control={
                        <Switch
                          checked={chargeMileageToCustomer}
                          onChange={(e) =>
                            setChargeMileageToCustomer(e.target.checked)
                          }
                        />
                      }
                    />
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <TextField
                      value={accomodation}
                      onChange={(e) =>
                        e.target.value >= 0 &&
                        setAccomodation(parseFloat(e.target.value))
                      }
                      fullWidth
                      type="number"
                      label="Accommodation"
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {orgSettings?.currency ?? '£'}
                          </InputAdornment>
                        ),
                        inputProps: { min: 0 },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <FormControlLabel
                      label="Charge Accommodation to Customer"
                      control={
                        <Switch
                          checked={chargeAccomToCustomer}
                          onChange={(e) =>
                            setChargeAccomToCustomer(e.target.checked)
                          }
                        />
                      }
                    />
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <TextField
                      value={treatmentCentreBuild}
                      onChange={(e) =>
                        e.target.value >= 0 &&
                        setTreatmentCentreBuild(parseFloat(e.target.value))
                      }
                      InputProps={{
                        inputProps: { min: 0 },
                        startAdornment: (
                          <InputAdornment position="start">
                            {orgSettings?.currency ?? '£'}
                          </InputAdornment>
                        ),
                      }}
                      label="Treatment Centre Builds"
                      size="small"
                      type="number"
                      autoComplete="off"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <TextField
                      value={adminCharge}
                      onChange={(e) =>
                        e.target.value >= 0 &&
                        setAdminCharge(parseFloat(e.target.value))
                      }
                      label="Admin Charge"
                      size="small"
                      fullWidth
                      type="number"
                      autoComplete="off"
                      InputProps={{
                        inputProps: { min: 0 },

                        startAdornment: (
                          <InputAdornment position="start">
                            {orgSettings?.currency ?? '£'}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </DetailsBox>
            </Paper>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Stack gap={1}>
              {hasExistingQuote && !viewOnly && quoteMeta?.status === 'Draft' && (
                <Alert severity="info" variant="outlined">
                  You are editing this draft quote in place.
                </Alert>
              )}
              {hasExistingQuote && !viewOnly && quoteMeta && quoteMeta.status !== 'Draft' && (
                <Paper>
                  <Alert severity="warning" variant="outlined">
                    <AlertTitle>
                      This quote is {quoteMeta.status.toLowerCase()} — saving
                      creates a new version alongside it. Please provide a
                      summary of changes below.
                    </AlertTitle>
                    <TextField
                      label="Summary of Changes"
                      variant="standard"
                      value={summaryOfChanges}
                      onChange={(e) => setSummaryOfChanges(e.target.value)}
                      autoComplete="off"
                      name="quotebuilder-soc"
                      fullWidth
                      size="small"
                      required
                      multiline
                    />
                  </Alert>
                </Paper>
              )}
              {summaryOfChanges && viewOnly && (
                <Alert severity="info" icon={false}>
                  <AlertTitle>Summary of Changes</AlertTitle>
                  {summaryOfChanges}
                </Alert>
              )}
              <Paper sx={{ padding: 1 }}>
                <DetailsBox
                  title="Quote Summary"
                  rows={[
                    {
                      label: 'Personnel',
                      fullWidth: true,
                      value: formatMoney(displayPersonnel, orgSettings?.currency),
                    },
                    {
                      label: 'Vehicles & Mileage',
                      fullWidth: true,
                      value: formatMoney(displayVehiclesMileage, orgSettings?.currency),
                    },
                    {
                      label: 'Treatment Centre Build',
                      fullWidth: true,
                      value: formatMoney(treatmentCentreBuild, orgSettings?.currency),
                    },
                    {
                      label: 'Administration',
                      fullWidth: true,
                      value: formatMoney(adminCharge, orgSettings?.currency),
                    },
                  ]}
                >
                  <Typography variant="title3" fontSize="0.9rem" marginTop={1}>
                    Business Costs
                  </Typography>
                  <DetailRow
                    label="Scheduling"
                    value={formatMoney(displayScheduling, orgSettings?.currency)}
                  />
                  <DetailRow
                    label="Make Ready"
                    value={formatMoney(displayMakeReady, orgSettings?.currency)}
                  />
                  {!usingStoredBreakdown && (
                    <DetailRow
                      label="Mileage"
                      value={formatMoney(mileageTotal, orgSettings?.currency)}
                    />
                  )}
                  <DetailRow
                    label="Accommodation"
                    value={formatMoney(displayAccomodation, orgSettings?.currency)}
                  />
                  <Stack>
                    <Typography
                      variant="title3"
                      fontSize="0.9rem"
                      marginTop={1}
                    >
                      Profit Margin
                    </Typography>
                    <Stack direction="row" padding={1} gap={2}>
                      <Slider
                        value={profitMargin}
                        color={
                          profitMargin >= 40
                            ? 'success'
                            : profitMargin >= 20
                              ? 'warning'
                              : 'error'
                        }
                        onChange={(e) =>
                          setProfitMargin(parseInt(e.target.value))
                        }
                      />
                      <TextField
                        variant="standard"
                        autoComplete="off"
                        value={profitMargin}
                        onChange={(e) =>
                          e.target.value === ''
                            ? setProfitMargin(0)
                            : e.target.value >= 0 &&
                              e.target.value <= 100 &&
                              setProfitMargin(parseInt(e.target.value))
                        }
                        size="small"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                        }}
                        type="number"
                        sx={{ width: '100px' }}
                      />
                    </Stack>
                  </Stack>
                  <DetailRow
                    label="Total Invoice"
                    value={formatMoney(displayTotalInvoice, orgSettings?.currency)}
                  />
                  <DetailRow
                    label="Total Cost"
                    value={formatMoney(displayTotalCost, orgSettings?.currency)}
                  />
                  <DetailRow
                    label="Discount"
                    value={
                      <TextField
                        value={discount}
                        onChange={(e) =>
                          e.target.value >= 0 && e.target.value !== ''
                            ? setDiscount(parseFloat(e.target.value))
                            : setDiscount(0)
                        }
                        InputProps={{
                          inputProps: { min: 0 },
                          startAdornment: (
                            <InputAdornment position="start">
                              {orgSettings?.currency ?? '£'}
                            </InputAdornment>
                          ),
                        }}
                        size="small"
                        type="number"
                        autoComplete="off"
                        fullWidth
                      />
                    }
                  />
                  <DetailRow
                    label="Amount Due"
                    value={formatMoney(displayTotalInvoice - discount, orgSettings?.currency)}
                  />
                  <DetailRow
                    label="Total Profit"
                    value={formatMoney(displayTotalInvoice - discount - displayTotalCost, orgSettings?.currency)}
                  />
                </DetailsBox>
              </Paper>
              <Button
                type="submit"
                variant="contained"
                color="success"
                disabled={viewOnly}
              >
                {viewOnly ? 'View Only' : 'Submit'}
              </Button>
              {/* Send/Accept/Decline act on the SAVED quote, so they only
                  show in view mode - in edit mode they used to fire with
                  unsaved figures on screen (and, for a new version, against
                  the old quote entirely) */}
              {viewOnly && quoteMeta && ['Draft', 'Sent'].includes(quoteMeta.status) && (
                <Stack direction="row" gap={1}>
                  <Button
                    variant="outlined"
                    fullWidth
                    disabled={actioning}
                    onClick={handleSend}
                  >
                    {quoteMeta.status === 'Sent' ? 'Resend' : 'Send'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="anGreen"
                    fullWidth
                    disabled={actioning}
                    onClick={handleAccept}
                  >
                    Accept
                  </Button>
                  <Button
                    variant="outlined"
                    color="anRed"
                    fullWidth
                    disabled={actioning}
                    onClick={() => setOpenDecline(true)}
                  >
                    Decline
                  </Button>
                </Stack>
              )}
              {quoteMeta?.status === 'Accepted' && (
                <Alert severity="success">
                  Accepted{' '}
                  {quoteMeta.acceptedAt &&
                    moment(quoteMeta.acceptedAt).format('Do MMM YYYY')}
                </Alert>
              )}
              {quoteMeta?.status === 'Declined' && (
                <Alert severity="error">
                  Declined{' '}
                  {quoteMeta.declinedAt &&
                    moment(quoteMeta.declinedAt).format('Do MMM YYYY')}
                  {quoteMeta.declineReason && ` — ${quoteMeta.declineReason}`}
                </Alert>
              )}
            </Stack>
          </Grid>
        </Grid>
      )}
      <SmallModal open={openDecline} onClose={() => setOpenDecline(false)}>
        <Stack gap={1}>
          <Typography variant="title3">Decline Quote</Typography>
          <TextField
            label="Reason (optional)"
            value={declineReasonInput}
            onChange={(e) => setDeclineReasonInput(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
          <Stack direction="row" gap={1}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setOpenDecline(false)}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="anRed"
              disabled={actioning}
              onClick={handleDecline}
            >
              Confirm Decline
            </Button>
          </Stack>
        </Stack>
      </SmallModal>
    </Box>
  )
}

export default QuoteBuilder
